import { createContext, useContext, useMemo, useState } from "react";
import { bootstrap } from "../lib/bootstrap";
import { storage } from "../lib/storage";
import { resolveScope, ROLES, STATUS, isActive } from "../lib/auth";

const AuthContext = createContext(null);

// Boot state is shared with DataContext through this module-level cache so the
// expensive bootstrap is guaranteed to run exactly once.
let bootCache = null;
function getBoot() {
    if (!bootCache) bootCache = bootstrap();
    return bootCache;
}

export function AuthProvider({ children }) {
    const initial = useState(() => getBoot())[0];
    const [teachers, setTeachers] = useState(initial.teachers);
    const [currentUserId, _setCurrentUserId] = useState(initial.activeUserId);
    const [workspaceMode, _setWorkspaceMode] = useState(initial.workspaceMode);

    const currentUser = useMemo(
        () => teachers.find((t) => t.id === currentUserId) || teachers[0] || null,
        [teachers, currentUserId],
    );
    const scope = resolveScope(currentUser, workspaceMode);

    // If the active user gets deactivated, fall back to the first active
    // teacher (or admin if no teacher is active).
    const safeCurrentUser = useMemo(() => {
        if (!currentUser) return null;
        if (isActive(currentUser)) return currentUser;
        const fallback =
            teachers.find((t) => t.role === ROLES.TEACHER && isActive(t)) ||
            teachers.find((t) => t.role === ROLES.ADMIN && isActive(t));
        return fallback || null;
    }, [currentUser, teachers]);

    const needsSetup = teachers.length === 0;

    const api = useMemo(
        () => ({
            teachers,
            currentUser: safeCurrentUser,
            isAdmin: safeCurrentUser?.role === ROLES.ADMIN,
            workspaceMode,
            scope: resolveScope(safeCurrentUser, workspaceMode),
            needsSetup,
            /**
             * First-launch only. Creates the Studio Admin and (optionally) a
             * first teacher, then signs the admin in. Idempotent: refuses to
             * run if any teachers already exist.
             */
            completeSetup({ admin, firstTeacher }) {
                if (teachers.length > 0) {
                    throw new Error("Studio is already set up.");
                }
                const now = new Date().toISOString();
                const adminRecord = {
                    id: `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
                    name: admin.name.trim(),
                    email: (admin.email || "").trim(),
                    role: ROLES.ADMIN,
                    status: STATUS.ACTIVE,
                    createdAt: now,
                };
                const next = [adminRecord];
                if (firstTeacher && firstTeacher.name && firstTeacher.name.trim()) {
                    next.push({
                        id: `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}t`,
                        name: firstTeacher.name.trim(),
                        email: (firstTeacher.email || "").trim(),
                        role: ROLES.TEACHER,
                        status: STATUS.ACTIVE,
                        createdAt: now,
                    });
                }
                setTeachers(next);
                storage.saveTeachers(next);
                _setCurrentUserId(adminRecord.id);
                storage.setActiveUserId(adminRecord.id);
                _setWorkspaceMode("all");
                storage.setWorkspaceMode("all");
                return adminRecord;
            },
            setCurrentUserId(id) {
                _setCurrentUserId(id);
                storage.setActiveUserId(id);
                // If non-admin, workspace mode is irrelevant; reset for safety.
                const next = teachers.find((t) => t.id === id);
                if (next && next.role === ROLES.TEACHER) {
                    _setWorkspaceMode("all");
                    storage.setWorkspaceMode("all");
                }
            },
            setWorkspaceMode(mode) {
                _setWorkspaceMode(mode);
                storage.setWorkspaceMode(mode);
            },
            addTeacher({ name, email }) {
                // TODO(supabase-auth): replace local-only creation with sending
                //   a magic-link invite and inserting a row in the `teachers`
                //   table linked to the resulting Supabase auth.user.id.
                const t = {
                    id: `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
                    name: name.trim(),
                    email: (email || "").trim(),
                    role: ROLES.TEACHER,
                    status: STATUS.ACTIVE,
                    createdAt: new Date().toISOString(),
                };
                const next = [...teachers, t];
                setTeachers(next);
                storage.saveTeachers(next);
                return t;
            },
            renameTeacher(id, { name, email }) {
                const now = new Date().toISOString();
                const next = teachers.map((t) =>
                    t.id === id
                        ? {
                              ...t,
                              ...(name !== undefined ? { name: name.trim() } : {}),
                              ...(email !== undefined ? { email: email.trim() } : {}),
                              updatedAt: now,
                          }
                        : t,
                );
                setTeachers(next);
                storage.saveTeachers(next);
            },
            setTeacherStatus(id, status) {
                // Admins cannot be deactivated via this MVP.
                const target = teachers.find((t) => t.id === id);
                if (!target || target.role === ROLES.ADMIN) return;
                const now = new Date().toISOString();
                const next = teachers.map((t) =>
                    t.id === id ? { ...t, status, updatedAt: now } : t,
                );
                setTeachers(next);
                storage.saveTeachers(next);
                // If the currently selected user was just deactivated, switch.
                if (status === STATUS.INACTIVE && currentUserId === id) {
                    const fallback =
                        next.find(
                            (t) => t.role === ROLES.TEACHER && isActive(t),
                        ) || next.find((t) => t.role === ROLES.ADMIN);
                    if (fallback) {
                        _setCurrentUserId(fallback.id);
                        storage.setActiveUserId(fallback.id);
                    }
                }
                // If admin's workspace filter pointed to a deactivated teacher,
                // fall back to "all".
                if (status === STATUS.INACTIVE && workspaceMode === id) {
                    _setWorkspaceMode("all");
                    storage.setWorkspaceMode("all");
                }
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [teachers, safeCurrentUser, workspaceMode, currentUserId, needsSetup],
    );

    return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
